import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { EntitlementDeniedState, LoadingState } from "@odingard/design-system";
import { useAuth } from "../lib/auth";
import { useAsync } from "../lib/useAsync";
import { api } from "../lib/api";

export const VENDOR_ENTITLEMENT = "vendor_ai.enabled";

/** True when the organization actually holds Vendor AI. */
export function hasVendorEntitlement(entitlements: { key: string; status: string }[] | null): boolean {
  return entitlements?.some((e) => e.key === VENDOR_ENTITLEMENT && ["ALLOWED", "TRIAL"].includes(e.status)) ?? false;
}

// Fail-closed route guard for Vendor AI. The Worker re-checks membership, the
// entitlement and the acting user's Vendor AI permission on every call; this
// only keeps unentitled users out of the UI.
export function VendorGuard({ children }: { children: ReactNode }) {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(async () => (await api.entitlements(orgId)).entitlements, [orgId]);

  if (state.loading) return <LoadingState label="Checking your access…" />;
  if (!hasVendorEntitlement(state.data)) {
    return <EntitlementDeniedState product="Vendor AI" onUpgrade={() => navigate("/pricing")} />;
  }
  return <>{children}</>;
}
